import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { confirmDialog } from "../lib/uiDialogs";
import {
  Handshake,
  Plus,
  Trash,
  Power,
  Copy,
  Users,
  SpinnerGap,
  X,
  Check,
  ToggleLeft,
  ToggleRight,
  Eye,
  UserPlus
} from "@phosphor-icons/react";

import { API_BASE as API } from "../lib/apiBase";

export default function PartnerCodesPage() {
  const [codes, setCodes] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [requireCode, setRequireCode] = useState(true);  // Secure default (matches backend)
  const [togglingReq, setTogglingReq] = useState(false);
  const [formData, setFormData] = useState({ code: "", label: "", max_uses: "" });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [codesRes, settingsRes, usersRes] = await Promise.all([
        axios.get(`${API}/api/admin/partner-codes`, { withCredentials: true }),
        axios.get(`${API}/api/admin/partner-codes/settings`, { withCredentials: true }),
        axios.get(`${API}/api/admin/users`, { withCredentials: true }),
      ]);
      setCodes(codesRes.data);
      setRequireCode(settingsRes.data.require_partner_code);
      setUsers(usersRes.data);
    } catch (err) {
      console.error("Load error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        code: formData.code.trim().toUpperCase(),
        label: formData.label.trim(),
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
      };
      await axios.post(`${API}/api/admin/partner-codes`, payload, { withCredentials: true });
      toast.success("Code partenaire créé");
      setFormData({ code: "", label: "", max_uses: "" });
      setShowForm(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleToggle = async (codeId) => {
    try {
      const { data } = await axios.put(`${API}/api/admin/partner-codes/${codeId}/toggle`, {}, { withCredentials: true });
      toast.success(data.message);
      loadData();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const handleDelete = async (codeId) => {
    const ok = await confirmDialog("Supprimer ce code ?");
    if (!ok) return;
    try {
      await axios.delete(`${API}/api/admin/partner-codes/${codeId}`, { withCredentials: true });
      toast.success("Code supprimé");
      loadData();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const handleToggleRequire = async () => {
    setTogglingReq(true);
    try {
      const { data } = await axios.put(`${API}/api/admin/partner-codes/settings?require=${!requireCode}`, {}, { withCredentials: true });
      setRequireCode(data.require_partner_code);
      toast.success(data.message);
    } catch (err) {
      toast.error("Erreur");
    } finally {
      setTogglingReq(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Code copie !");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <SpinnerGap size={32} className="animate-spin text-[#002FA7]" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight font-['Outfit']" data-testid="partner-page-title">
            Codes Partenaires
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Gerez l'acces au programme via des codes d'invitation
          </p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-[#002FA7] hover:bg-[#002280]" data-testid="add-code-btn">
          <Plus size={16} className="mr-2" /> Nouveau code
        </Button>
      </div>

      {/* Require code toggle */}
      <div className="bg-white border border-zinc-200 rounded-sm p-4 mb-6 flex items-center justify-between" data-testid="require-code-toggle">
        <div>
          <h3 className="font-medium text-sm">Code partenaire obligatoire</h3>
          <p className="text-xs text-zinc-500 mt-0.5">
            {requireCode ? "Les nouveaux utilisateurs DOIVENT entrer un code pour s'inscrire" : "L'inscription est ouverte a tous (code optionnel)"}
          </p>
        </div>
        <button
          onClick={handleToggleRequire}
          disabled={togglingReq}
          className={`flex items-center gap-2 px-4 py-2 rounded-sm border text-sm font-medium transition-colors ${
            requireCode ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-zinc-600 border-zinc-300 hover:border-zinc-400"
          }`}
          data-testid="toggle-require-btn"
        >
          {requireCode ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
          {requireCode ? "Actif" : "Inactif"}
        </button>
      </div>

      {/* Create form */}
      {showForm && (
        <div className="bg-white border border-zinc-200 rounded-sm p-6 mb-6" data-testid="partner-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium">Nouveau code partenaire</h3>
            <button onClick={() => setShowForm(false)} className="p-1 hover:bg-zinc-100 rounded"><X size={18} /></button>
          </div>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-zinc-700 mb-1 block">Code</label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  placeholder="Ex: PARTNER2024"
                  required
                  className="uppercase"
                  data-testid="code-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-700 mb-1 block">Label</label>
                <Input
                  value={formData.label}
                  onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                  placeholder="Ex: Équipe Montréal"
                  required
                  data-testid="label-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-zinc-700 mb-1 block">Limite d'utilisation</label>
                <Input
                  type="number"
                  value={formData.max_uses}
                  onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                  placeholder="Illimite"
                  min="1"
                  data-testid="max-uses-input"
                />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Annuler</Button>
              <Button type="submit" className="bg-[#002FA7] hover:bg-[#002280]" data-testid="submit-code-btn">
                <Check size={16} className="mr-2" /> Créer
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Codes list */}
      {codes.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-sm p-12 text-center" data-testid="empty-codes">
          <Handshake size={48} className="mx-auto mb-3 text-zinc-300" />
          <h3 className="font-medium text-zinc-600">Aucun code partenaire</h3>
          <p className="text-sm text-zinc-400 mt-1">Creez un code pour inviter des partenaires</p>
        </div>
      ) : (
        <div className="space-y-3 mb-8" data-testid="codes-list">
          {codes.map((c) => (
            <div key={c.id} className={`bg-white border rounded-sm p-4 ${c.is_active ? "border-zinc-200" : "border-zinc-200 opacity-60"}`} data-testid={`code-card-${c.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`px-3 py-1.5 rounded font-mono text-sm font-bold tracking-wider ${c.is_active ? "bg-[#002FA7]/10 text-[#002FA7]" : "bg-zinc-100 text-zinc-400"}`}>
                    {c.code}
                  </div>
                  <div>
                    <div className="font-medium text-sm">{c.label}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">
                      {c.used_count} utilisation{c.used_count > 1 ? "s" : ""}
                      {c.max_uses ? ` / ${c.max_uses} max` : " (illimite)"}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => copyCode(c.code)} className="p-2 hover:bg-zinc-100 rounded-sm text-zinc-500" title="Copier" data-testid={`copy-code-${c.id}`}>
                    <Copy size={16} />
                  </button>
                  <button onClick={() => handleToggle(c.id)} className={`p-2 rounded-sm ${c.is_active ? "hover:bg-yellow-50 text-yellow-600" : "hover:bg-blue-50 text-blue-600"}`} title={c.is_active ? "Desactiver" : "Activer"} data-testid={`toggle-code-${c.id}`}>
                    <Power size={16} />
                  </button>
                  <button onClick={() => handleDelete(c.id)} className="p-2 hover:bg-red-50 rounded-sm text-zinc-500 hover:text-red-600" title="Supprimer" data-testid={`delete-code-${c.id}`}>
                    <Trash size={16} />
                  </button>
                </div>
              </div>
              {c.used_by?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-zinc-100">
                  <div className="text-xs text-zinc-500 mb-1">Utilise par :</div>
                  <div className="flex flex-wrap gap-1">
                    {c.used_by.map((u, i) => (
                      <span key={i} className="px-2 py-0.5 text-xs bg-zinc-100 text-zinc-600 rounded">{u.email}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Users section */}
      <div className="mt-8">
        <h2 className="text-lg font-semibold tracking-tight font-['Outfit'] mb-4 flex items-center gap-2">
          <Users size={20} /> Utilisateurs inscrits
        </h2>
        <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden" data-testid="users-table">
          <div className="divide-y divide-zinc-100">
            {users.map((u) => (
              <div key={u.id} className="px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${u.role === "admin" ? "bg-amber-500" : "bg-[#002FA7]"}`}>
                    {u.name?.charAt(0)?.toUpperCase() || "?"}
                  </div>
                  <div>
                    <div className="text-sm font-medium">{u.name}</div>
                    <div className="text-xs text-zinc-500">{u.email}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {u.partner_code && (
                    <span className="px-2 py-0.5 text-xs font-mono bg-zinc-100 text-zinc-600 rounded">{u.partner_code}</span>
                  )}
                  <span className={`px-2 py-0.5 text-xs rounded ${u.role === "admin" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}`}>
                    {u.role}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
